import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Delete from "@mui/icons-material/Delete";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import HourglassEmpty from "@mui/icons-material/HourglassEmpty";
import LinkOff from "@mui/icons-material/LinkOff";
import ListAlt from "@mui/icons-material/ListAlt";
import MoreVert from "@mui/icons-material/MoreVert";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { SparkleIcon } from "components/ArtificialIntelligence/SparkleIcon";
import { ConfirmDialog } from "components/ConfirmDialog";
import { DocumentSwitcherSelect } from "components/Document/ReviewDocumentMetadataDialog/components/DocumentSwitcherSelect";
import { ManifestDetailSection } from "components/Document/ReviewDocumentMetadataDialog/components/ManifestDetailSection";
import { useDocumentMetadata } from "components/Document/ReviewDocumentMetadataDialog/hooks/useDocumentMetadata";
import { useManifestReviewSection } from "components/Document/ReviewDocumentMetadataDialog/hooks/useManifestReviewSection";
import { DocumentDialogContainer } from "components/Document/shared";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { ExtractionStatus } from "generated-graphql/graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { ReactNode, useCallback, useMemo, useRef, useState } from "react";

export type ManifestReviewDialogProps = {
  onClose: () => void;
  /** Document IDs to review. When more than one, the dialog navigates between them. */
  documentIds: string[];
  /**
   * Pre-resolved waste manifest ID. When omitted, the dialog falls back to
   * the document's sourceWasteManifest (set after extraction completes).
   */
  wasteManifestId?: string;
  /** Called after a successful save+next on the last document. */
  onComplete?: () => void;
};

/**
 * Manifest-only review dialog. Single-form layout (no tabs):
 *  - Title bar: Info-icon Popover with doc metadata + an "Open in Documents"
 *    deep link; More-Actions menu (Detach/Delete).
 *  - Left pane: manifest fields form
 *  - Right pane: PDF preview
 *
 * Distinct from the shared ReviewDocumentMetadataDialog (which is general
 * document AI). Manifest entry points (ManifestUploadDialog, the Documents
 * section's source-document action) open this dialog. Reuses
 * ManifestDetailSection + useManifestReviewSection.
 */
export function ManifestReviewDialog({
  onClose,
  documentIds,
  wasteManifestId: initialWasteManifestId,
  onComplete,
}: ManifestReviewDialogProps) {
  const alerts = useAlerts();
  const { featureFlags } = useFeatureFlags();
  const hasDocumentAI = !!featureFlags[FeatureFlagKey.DocumentAI];
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [currentDocumentId, setCurrentDocumentId] = useState<string | undefined>(
    () => documentIds[0]
  );
  const [moreActionsAnchor, setMoreActionsAnchor] = useState<null | HTMLElement>(null);
  const [docInfoAnchor, setDocInfoAnchor] = useState<null | HTMLElement>(null);
  const [showDetachConfirm, setShowDetachConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);

  const currentIndex = useMemo(() => {
    if (!currentDocumentId) return 0;
    const i = documentIds.indexOf(currentDocumentId);
    return i >= 0 ? i : 0;
  }, [currentDocumentId, documentIds]);
  const hasNext = currentIndex < documentIds.length - 1;

  const { document, loading, previewUrl } = useDocumentMetadata({
    documentId: currentDocumentId,
    open: true,
    hasDocumentAI,
  });

  const resolvedWasteManifestId =
    initialWasteManifestId ?? document?.sourceWasteManifest?.id ?? undefined;

  const manifest = useManifestReviewSection({
    wasteManifestId: resolvedWasteManifestId,
    documentId: currentDocumentId,
  });

  const navigateToDocument = useCallback((docId: string) => {
    setCurrentDocumentId(docId);
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Going backwards in a multi-doc review: navigate without saving. The user
  // can hit Save & Next on the way forward again if they want to commit.
  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      navigateToDocument(documentIds[currentIndex - 1]);
    }
  }, [currentIndex, documentIds, navigateToDocument]);

  // Skip the current doc forward without saving. Confirms first so users
  // understand the doc stays in needs-review state until they come back to it.
  const handleSkipConfirm = useCallback(() => {
    setShowSkipConfirm(false);
    if (currentIndex < documentIds.length - 1) {
      navigateToDocument(documentIds[currentIndex + 1]);
    }
  }, [currentIndex, documentIds, navigateToDocument]);

  // Jump to any doc in the batch via the title-bar switcher.
  const handleSwitcherSelect = useCallback(
    (docId: string) => {
      if (docId !== currentDocumentId) {
        navigateToDocument(docId);
      }
    },
    [currentDocumentId, navigateToDocument]
  );

  // Wrap close attempts: if the form is dirty, prompt to discard. Otherwise
  // close immediately. Used by the X button, Cancel button, and backdrop clicks.
  // When closing an unreviewed doc with no pending edits, surface a brief
  // reassurance — the uploaded document is saved and attached (just not yet
  // reviewed), so closing here isn't a lost-work dead end.
  const attemptClose = useCallback(() => {
    if (manifest.manifestIsDirty) {
      setShowDiscardConfirm(true);
    } else {
      if (manifest.manifestNeedsReview) {
        alerts.info("Manifest document saved. It's attached and still marked Needs Review.");
      }
      onClose();
    }
  }, [manifest.manifestIsDirty, manifest.manifestNeedsReview, onClose, alerts]);

  // After detach/delete: if there are more docs in the queue, advance to the
  // next one so the user can keep reviewing. Otherwise close. Without this,
  // a destructive action on doc N collapses the rest of the queue.
  const advanceOrClose = useCallback(() => {
    if (hasNext) {
      navigateToDocument(documentIds[currentIndex + 1]);
    } else {
      onClose();
    }
  }, [hasNext, currentIndex, documentIds, navigateToDocument, onClose]);

  // Primary action: when the doc still needs review this marks it reviewed
  // (clearing needsReview); once reviewed it's a plain edit-save. Either way it
  // advances to the next doc / closes on the last.
  const handleSaveAndNext = useCallback(async () => {
    const markReviewed = manifest.manifestNeedsReview;
    try {
      await manifest.saveManifest({ markReviewed });
      if (markReviewed || manifest.manifestIsDirty) {
        alerts.success(markReviewed ? "Manifest marked as reviewed." : "Manifest saved.");
      }
      if (hasNext) {
        navigateToDocument(documentIds[currentIndex + 1]);
      } else {
        onComplete?.();
        onClose();
      }
    } catch (err) {
      alerts.error("Failed to save manifest.", err instanceof Error ? err : undefined);
    }
  }, [
    manifest,
    hasNext,
    currentIndex,
    documentIds,
    navigateToDocument,
    onComplete,
    onClose,
    alerts,
  ]);

  // Partial save: persist edits without marking the doc reviewed, and stay on
  // it. Lets a reviewer pause mid-document (e.g. to ask about a handwritten line
  // item) without losing work or prematurely clearing the Needs Review flag.
  const handlePartialSave = useCallback(async () => {
    try {
      await manifest.saveManifest({ markReviewed: false });
      alerts.success("Progress saved. This manifest stays marked Needs Review.");
    } catch (err) {
      alerts.error("Failed to save manifest.", err instanceof Error ? err : undefined);
    }
  }, [manifest, alerts]);

  const isSaving = manifest.manifestSaving;
  const isLoading = loading || manifest.manifestLoading;
  const isBusy = isSaving || isLoading;

  const needsReview = manifest.manifestNeedsReview;
  // Still-flagged data reads as a review task; once reviewed it's an edit of
  // the saved document data.
  const dialogTitle = needsReview ? "Review Manifest Document Data" : "Edit Manifest Document Data";
  let primaryActionLabel: string;
  if (isSaving) {
    primaryActionLabel = "Saving…";
  } else {
    // When still flagged, the primary action marks the doc reviewed — name it so
    // (not "Confirm"); once reviewed it's a plain Save. During initial load the
    // button is disabled (via isBusy) but keeps its normal label.
    const verb = needsReview ? "Mark Reviewed" : "Save";
    primaryActionLabel = hasNext ? `${verb} & Next` : verb;
  }

  const canShowActions = !!currentDocumentId && manifest.manifestDataLoaded;
  const tags = document?.documentTags ?? [];
  const authoredAtDisplay = document?.authoredAt
    ? getUtcDisplayDate(document.authoredAt, "med")
    : "—";

  // Popover content: doc metadata + a deep link out to the document library.
  // Triggered by the "i" Info icon next to the More-Actions menu in the title bar.
  const documentLibraryHref =
    document?.tenantId && document?.id ? `/o/${document.tenantId}/documents/${document.id}` : null;
  const docInfoPopover = (
    <Stack spacing={1.25} sx={{ p: 2, maxWidth: 360 }}>
      <InfoRow label="Title" value={document?.title ?? "—"} />
      <InfoRow label="Document type" value={document?.documentType ?? "—"} />
      <InfoRow
        label="File"
        value={document?.fileExtension ? document.fileExtension.toUpperCase() : "—"}
      />
      <InfoRow label="Date authored" value={authoredAtDisplay} />
      <InfoRow label="Description" value={document?.description ?? "—"} />
      <InfoRow
        label="Tags"
        value={
          tags.length === 0 ? (
            "—"
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {tags.map((tag) => (
                <Chip key={tag.id} label={tag.name} size="small" variant="outlined" />
              ))}
            </Stack>
          )
        }
      />
      {documentLibraryHref && (
        <Link
          href={documentLibraryHref}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          onClick={() => setDocInfoAnchor(null)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            pt: 0.5,
          }}
        >
          Manage document
          <OpenInNew fontSize="inherit" />
        </Link>
      )}
    </Stack>
  );

  return (
    <>
      <DocumentDialogContainer
        open
        onClose={attemptClose}
        fullWidth
        maxWidth="lg"
        title={dialogTitle}
        titleBarActionElement={
          canShowActions ? (
            <>
              {documentIds.length > 1 && (
                <DocumentSwitcherSelect
                  documentIds={documentIds}
                  tenantId={document?.tenantId ?? undefined}
                  selectedId={currentDocumentId}
                  onSelect={handleSwitcherSelect}
                  minWidth={260}
                  maxWidth={300}
                />
              )}
              {manifest.manifestNeedsReview && (
                <Chip
                  icon={<SparkleIcon sx={{ fontSize: 16 }} />}
                  label="Needs review"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ mr: 0.5 }}
                />
              )}
              <Tooltip title="Document info">
                <IconButton
                  size="small"
                  onClick={(e) => setDocInfoAnchor(e.currentTarget)}
                  aria-label="Document info"
                >
                  <ListAlt />
                </IconButton>
              </Tooltip>
              <Popover
                open={Boolean(docInfoAnchor)}
                anchorEl={docInfoAnchor}
                onClose={() => setDocInfoAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                {docInfoPopover}
              </Popover>
              <Tooltip title="More actions">
                <IconButton
                  size="small"
                  onClick={(e) => setMoreActionsAnchor(e.currentTarget)}
                  aria-label="More actions"
                >
                  <MoreVert />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={moreActionsAnchor}
                open={Boolean(moreActionsAnchor)}
                onClose={() => setMoreActionsAnchor(null)}
              >
                <MenuItem
                  onClick={() => {
                    setMoreActionsAnchor(null);
                    setShowDetachConfirm(true);
                  }}
                >
                  <ListItemIcon>
                    <LinkOff fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Detach data</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setMoreActionsAnchor(null);
                    setShowDeleteConfirm(true);
                  }}
                >
                  <ListItemIcon>
                    <Delete fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText>Delete document</ListItemText>
                </MenuItem>
              </Menu>
            </>
          ) : undefined
        }
        // Fixed dialog height so the preview pane (which uses height: 100%)
        // has an anchor and renders correctly.
        sx={{
          "& .MuiDialog-paper": {
            height: "calc(100vh - 80px)",
            maxHeight: "1100px",
          },
        }}
        contentSx={{ pt: 0 }}
        // Pin the outer DialogContent so it doesn't scroll — that lets the form
        // column and preview column scroll independently inside it.
        DialogContentProps={{ sx: { overflowY: "hidden" } }}
        // The form column is `<Grid container>` (flex with wrap), so its bounded
        // height isn't reliable as a scroll target. Make it `position: relative`
        // so an absolutely-positioned inner Box can fill its bounds and scroll.
        formContainerSx={{
          position: "relative",
          overflowY: "visible",
        }}
        previewUrl={previewUrl}
        fileExtension={document?.fileExtension ?? undefined}
        previewLoading={loading}
        showPreviewBoxIfFailed
        formColumnSize={{ xs: 6 }}
        previewColumnSize={{ xs: 6 }}
        hideCancelButton={false}
        cancelButtonProps={{ text: "Close" }}
        additionalPrimaryActionElements={[
          ...(currentIndex > 0
            ? [
                <Button
                  key="back"
                  variant="outlined"
                  disabled={isBusy}
                  onClick={handlePrevious}
                  startIcon={<ChevronLeft />}
                >
                  Back
                </Button>,
              ]
            : []),
          ...(hasNext
            ? [
                <Button
                  key="skip"
                  variant="text"
                  disabled={isBusy}
                  onClick={() => setShowSkipConfirm(true)}
                  sx={{ color: "text.secondary" }}
                >
                  Skip
                </Button>,
              ]
            : []),
          // Partial save — only while the doc needs review (once reviewed the
          // primary "Save" already covers plain edits). Enabled only when there
          // are edits to persist; keeps the doc in Needs Review.
          ...(needsReview
            ? [
                <Button
                  key="partial-save"
                  variant="outlined"
                  disabled={isBusy || !manifest.manifestIsDirty}
                  onClick={handlePartialSave}
                >
                  Save
                </Button>,
              ]
            : []),
        ]}
        primaryActionButton={
          <Button
            variant="contained"
            disabled={isBusy}
            onClick={handleSaveAndNext}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
            endIcon={hasNext && !isSaving ? <ChevronRight /> : undefined}
          >
            {primaryActionLabel}
          </Button>
        }
      >
        {/* Absolute-positioned inner Box is the scroll surface. Parent form
          column is `position: relative` so this Box fills its exact bounds
          regardless of how the Grid v2 container computes its height. */}
        <Box
          ref={scrollContainerRef}
          sx={{ position: "absolute", inset: 0, overflowY: "auto", pr: 2 }}
        >
          <Stack spacing={2} sx={{ pt: 1, minWidth: 0 }}>
            {loading || manifest.manifestLoading ? (
              <ManifestReviewSkeleton />
            ) : !manifest.manifestDataLoaded ? (
              <NoManifestDataState
                extractionStatus={document?.extractionStatus ?? null}
                extractionError={document?.extractionError ?? null}
              />
            ) : (
              <Box sx={{ minWidth: 0 }}>
                <ManifestDetailSection manifest={manifest} />
              </Box>
            )}
          </Stack>
        </Box>
      </DocumentDialogContainer>

      {showDetachConfirm && (
        <ConfirmDialog
          open
          onClose={() => setShowDetachConfirm(false)}
          onConfirm={async () => {
            try {
              await manifest.handleDetached();
              alerts.success("Manifest data detached. The source document has been kept.");
              setShowDetachConfirm(false);
              advanceOrClose();
            } catch (err) {
              alerts.error(
                "Failed to detach manifest data.",
                err instanceof Error ? err : undefined
              );
              setShowDetachConfirm(false);
            }
          }}
          header="Detach manifest data?"
          info="This will delete the parsed manifest data (waste manifest, waste lines, and transporters) but keep the source document."
          confirmText="Detach"
          loading={manifest.deletingManifest}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmDialog
          open
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={async () => {
            try {
              await manifest.handleDeleteDocument();
              setShowDeleteConfirm(false);
              advanceOrClose();
            } catch {
              // handleDeleteDocument has already surfaced a specific alert
              // (partial-failure or detach-failure). Just dismiss the confirm.
              setShowDeleteConfirm(false);
            }
          }}
          header="Delete document?"
          info="This will permanently delete the source document and all associated manifest data (waste manifest, waste lines, and transporters). This cannot be undone."
          confirmText="Delete"
          loading={manifest.deletingDocument || manifest.deletingManifest}
        />
      )}

      {showDiscardConfirm && (
        <ConfirmDialog
          open
          onClose={() => setShowDiscardConfirm(false)}
          onConfirm={() => {
            setShowDiscardConfirm(false);
            onClose();
          }}
          header="Discard unsaved changes?"
          info="You have unsaved changes to this manifest. Closing now will lose them."
          confirmText="Discard"
        />
      )}

      {showSkipConfirm && (
        <ConfirmDialog
          open
          onClose={() => setShowSkipConfirm(false)}
          onConfirm={handleSkipConfirm}
          header="Skip without reviewing?"
          info="You can come back to this manifest at any time. It will stay marked as Needs Review until you mark it reviewed."
          confirmText="Skip"
        />
      )}
    </>
  );
}

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120, flexShrink: 0 }}>
        {label}
      </Typography>
      {typeof value === "string" ? (
        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
          {value}
        </Typography>
      ) : (
        value
      )}
    </Stack>
  );
}

/**
 * Empty state for the form pane when there's a source document but no parsed
 * manifest data — extraction is still in flight, never ran, or failed. Without
 * this the dialog would sit in the skeleton state forever for these docs.
 * Copy adapts to the document's extractionStatus.
 */
function NoManifestDataState({
  extractionStatus,
  extractionError,
}: {
  extractionStatus: ExtractionStatus | null;
  extractionError: string | null;
}) {
  const isFailed = extractionStatus === ExtractionStatus.Failed;
  const isPending =
    extractionStatus === ExtractionStatus.Pending ||
    extractionStatus === ExtractionStatus.NotAttempted ||
    extractionStatus === null;
  const Icon = isFailed ? ErrorOutline : HourglassEmpty;
  const heading = isFailed
    ? "Couldn't parse this manifest"
    : isPending
      ? "Manifest data is still being parsed"
      : "No manifest data yet";
  const body = isFailed
    ? extractionError ||
      "Parsing failed for this document. Re-parse from the staff Manifest Documents page, or review the source PDF on the right."
    : isPending
      ? "We're still extracting fields from the document. Refresh in a minute, or review the source PDF on the right while you wait."
      : "There's no parsed manifest record linked to this document. Review the source PDF on the right.";
  return (
    <Stack spacing={1.5} alignItems="center" sx={{ p: 4, textAlign: "center" }}>
      <Icon color={isFailed ? "error" : "action"} sx={{ fontSize: 40 }} />
      <Typography variant="subtitle1">{heading}</Typography>
      <Typography variant="body2" color="text.secondary">
        {body}
      </Typography>
    </Stack>
  );
}

/**
 * Loading-state shape that mirrors the eventual ManifestDetailSection layout —
 * section headers + grouped field skeletons. Cleaner than showing the real
 * accordion with empty/placeholder values while data is in flight.
 */
function ManifestReviewSkeleton() {
  return (
    <Stack spacing={3} sx={{ p: 1 }}>
      {/* Help text */}
      <Skeleton variant="text" width="80%" height={20} />

      {/* Generator ID + Tracking Number */}
      <Skeleton variant="rounded" height={48} />
      <Skeleton variant="rounded" height={48} />

      {/* Generator: name + mailing + site address */}
      <Skeleton variant="text" width={140} height={28} />
      <Skeleton variant="rounded" height={48} />
      <Skeleton variant="rounded" height={48} />
      <Stack direction="row" spacing={2}>
        <Skeleton variant="rounded" height={48} sx={{ flex: 2 }} />
        <Skeleton variant="rounded" height={48} sx={{ flex: 1 }} />
        <Skeleton variant="rounded" height={48} sx={{ flex: 1.5 }} />
      </Stack>
      <Skeleton variant="rounded" height={48} />
      <Stack direction="row" spacing={2}>
        <Skeleton variant="rounded" height={48} sx={{ flex: 2 }} />
        <Skeleton variant="rounded" height={48} sx={{ flex: 1 }} />
        <Skeleton variant="rounded" height={48} sx={{ flex: 1.5 }} />
      </Stack>

      {/* Transporters (collapsed accordion) */}
      <Skeleton variant="text" width={200} height={28} />
      <Skeleton variant="rounded" height={56} />

      {/* Designated facility */}
      <Skeleton variant="text" width={200} height={28} />
      <Skeleton variant="rounded" height={48} />
      <Skeleton variant="rounded" height={48} />
      <Skeleton variant="rounded" height={48} />
      <Stack direction="row" spacing={2}>
        <Skeleton variant="rounded" height={48} sx={{ flex: 2 }} />
        <Skeleton variant="rounded" height={48} sx={{ flex: 1 }} />
        <Skeleton variant="rounded" height={48} sx={{ flex: 1.5 }} />
      </Stack>

      {/* Line items (collapsed accordion) */}
      <Skeleton variant="text" width={180} height={28} />
      <Skeleton variant="rounded" height={56} />

      {/* Shipped + received dates */}
      <Stack direction="row" spacing={2}>
        <Skeleton variant="rounded" height={48} sx={{ flex: 1 }} />
        <Skeleton variant="rounded" height={48} sx={{ flex: 1 }} />
      </Stack>
    </Stack>
  );
}
